/**
 * studentTutoringViewModel — pure `(reduxState) → props` de « Messages » (`<StudentCoachPage>`).
 *
 * Patron A : le compte (`currentUser.object`, relu par `getUser`). Port de `MyCoachTabScreen.js`.
 * L'onglet garde les tuteurs et leurs conversations ; ses prochains lives sont partis sur l'accueil
 * et dans « Tous mes lives » (Wajih, 18/10 — `studentLiveSessionsListViewModel`).
 *
 * Mémoïsé (`createSelector`) sur les tranches lues : l'onglet reste monté derrière les autres.
 */
import { createSelector } from "reselect"

import { type ApiSchoolSubject } from "../../../adapters/parent/shared/schoolSubjectAdapter"
import {
    studentNoTutor,
    studentUnreadMessagesCount,
    toStudentTutors,
    type ApiStudentTutoringUser,
} from "../../../adapters/student/Tutoring/studentTutoringAdapter"
import { studentOffer, type StudentOffer } from "../../../adapters/student/shared/studentOfferAdapter"
import { type UITutor } from "../../../ui/types"
import { type UIStudentNoTutor, type UIStudentTutor } from "../../../ui/types/studentTutoring"
import { selectDisplayedStudent } from "../../shared/displayedStudent"

export type StudentTutoringViewModel = {
    tutors: UIStudentTutor[]
    /** Le tuteur principal : sa conversation s'ouvre sans `tutorId`, comme aujourd'hui. */
    mainTutorId: UITutor["id"] | null
    noTutor?: UIStudentNoTutor
    offer: StudentOffer | null
    /** Le compte est relu (le bouton de la barre de titre) : seul le bouton tourne. */
    refreshing: boolean
}

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
type ReduxState = any
type ApiSlice = { request?: boolean }

/**
 * Ce que la page lit du compte, dans son propre sélecteur : une relecture du compte en cours
 * (`getUser`) ne refait pas la liste des tuteurs.
 */
const selectTutoringContent = createSelector(
    [
        (state: ReduxState) => selectDisplayedStudent(state) as ApiStudentTutoringUser | null,
        (state: ReduxState) => state?.data?.staticData?.school_subjects as ApiSchoolSubject[] | undefined,
    ],
    (user, subjectsOrNil) => {
        const { tutors, mainTutorId } = toStudentTutors(user, subjectsOrNil ?? [])
        return { tutors, mainTutorId, noTutor: studentNoTutor(user), offer: studentOffer(user) }
    }
)

/**
 * La pastille de l'onglet « Messages », lue par la barre d'onglets (`StudentTabBar`) : ses messages
 * non lus seulement (`studentUnreadMessagesCount`). Un nombre : la barre ne se redessine que s'il change.
 */
export const studentMessagesBadge = (state: ReduxState): number =>
    studentUnreadMessagesCount(selectDisplayedStudent(state) as ApiStudentTutoringUser | null | undefined)

export const studentTutoringViewModel = createSelector(
    [selectTutoringContent, (state: ReduxState) => !!(state?.api?.getUser as ApiSlice | undefined)?.request],
    (content, refreshing): StudentTutoringViewModel => ({ ...content, refreshing })
)
